import React, { useEffect } from 'react'

import { Box, Checkbox, RadioButton } from '@factorialco/deprecated-design-system'
import { F0Icon } from '@factorialco/f0-react'
import { CheckCircle, CrossedCircle } from '@factorialco/f0-react/icons/app'
import { xor } from 'lodash'

import { Element } from 'modules/forms/lib/Element'
import { Answer } from 'modules/forms/lib/types'

interface OptionElementQuestionProps {
  element: Element
  hasAttemptedSubmit?: boolean
  isSingleChoice: boolean
  onChange?: (answer: Answer) => void
  disabled?: boolean
  showCorrectAnswerIcons?: boolean
}

/**
 * Editor component for TextElement
 */
export const OptionElementQuestion: React.FC<OptionElementQuestionProps> = ({
  element,
  isSingleChoice,
  onChange,
  disabled,
  showCorrectAnswerIcons = true,
}) => {
  const [choiceAnswers, setChoiceAnswers] = React.useState<string[]>(() =>
    element.answer && element.answer.options ? element.answer.options : []
  )
  useEffect(() => {
    setChoiceAnswers(element.answer && element.answer.options ? element.answer.options : [])
  }, [element, element.answer])

  const handleChange = (optionId: string) => {
    const isSingleOptionSelected = optionId === choiceAnswers[0]
    const singleAnswer = isSingleChoice && isSingleOptionSelected ? [] : [optionId]
    const selectedChoices = isSingleChoice ? singleAnswer : xor(choiceAnswers, [optionId])
    setChoiceAnswers(selectedChoices)
    onChange?.({
      answer: selectedChoices.join(','),
      options: selectedChoices,
    })
  }

  const getOptionStyling = (option: Element['options'][0], checked: boolean) => {
    const styling: {
      border?: {
        all: {
          width: 's1'
          style: 'solid'
          color: 'group2' | 'moodSuperNegative'
        }
      }
      background?: 'grey200'
    } = {}

    if (!disabled || !element.hasCorrectAnswer) {
      return styling
    }

    // If showCorrectAnswerIcons is false, only show background for checked options
    if (!showCorrectAnswerIcons) {
      if (checked) {
        styling.background = 'grey200'
      }
      return styling
    }

    // If showCorrectAnswerIcons is true, show correct answer borders
    if (option.isCorrect) {
      styling.border = {
        all: {
          width: 's1',
          style: 'solid',
          color: 'group2',
        },
      }
      styling.background = 'grey200'
    } else if (checked) {
      styling.border = {
        all: {
          width: 's1',
          style: 'solid',
          color: 'moodSuperNegative',
        },
      }
      styling.background = 'grey200'
    }

    return styling
  }

  const hasCorrectAnswer = element.hasCorrectAnswer && showCorrectAnswerIcons

  return (
    <>
      {element.options.map((option, index) => {
        // Use index as fallback identifier for unsaved options (before saving to DB)
        // Use || instead of ?? because option.id can be an empty string ''
        // Use element.uuid + index as fallback to ensure uniqueness across different unsaved questions
        const optionValue = option.id || `${element.uuid}-${index}`
        const checked = choiceAnswers.includes(optionValue) || false
        const styling = getOptionStyling(option, checked)
        const isCorrect = option.isCorrect
        const Component = isSingleChoice ? RadioButton : Checkbox
        return (
          <Box
            key={optionValue}
            border={styling.border}
            borderRadius={{ all: 'abs012' }}
            background={styling.background}
            padding="s12"
          >
            <Box flexDirection="row" justifyContent="spaceBetween" alignItems="center" gap="s20">
              <Component
                key={optionValue}
                size="small"
                value={optionValue}
                label={option.label}
                disabled={disabled}
                checked={checked}
                onChange={() => handleChange(optionValue)}
              />

              {hasCorrectAnswer && !isCorrect && (
                <F0Icon icon={CrossedCircle} size="md" color="#FF5C4B" />
              )}
              {hasCorrectAnswer && isCorrect && (
                <F0Icon icon={CheckCircle} size="md" color="#10B881" />
              )}
            </Box>
          </Box>
        )
      })}
    </>
  )
}
